/**
 * Card "Vacinas" da ficha do funcionário (#530, spec D10). Uma linha por vacina exigida e, abaixo,
 * as que têm dose registrada sem serem exigidas. A situação vem pronta do servidor; o card só a
 * desenha. A vacina com dose registrada abre a lista das doses, com o comprovante de cada uma.
 */
import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Description as ComprovanteIcon,
  ExpandMore as ExpandMoreIcon,
  Vaccines as VaccinesIcon,
} from '@mui/icons-material';
import { pode } from '../../../authorization/pode';
import { useAcao } from '../../../authorization/useVeredito';
import { useDadoPermitido } from '../../../authorization/useListaPermitida';
import { RouteActionButton } from '../../../components/common';
import { vaccinationService } from '../../../services/vaccinationService';
import {
  SITUACAO_VACINAL_LABELS,
  type FichaDeVacinacao,
  type LinhaVacina,
  type SituacaoVacinal,
  type Vaccination,
} from '../../../types/vaccination';
import { dataBrasileira } from '../../../utils/vaccinationDates';
import { RegisterDoseDialog } from './RegisterDoseDialog';

interface EmployeeVaccinationsCardProps {
  employeeId: number | string;
  companyId: number | string;
  /** Fora do efetivo (demitido, inativo) o servidor devolve a ficha vazia de propósito. */
  ativo: boolean;
}

const ACCENT = '#00838F';
const COLUNAS = 'minmax(0, 1.4fr) minmax(0, 1.2fr) minmax(0, 1fr) auto';
/** O nome da vacina começa depois do botão que abre as doses; o título da coluna acompanha. */
const RECUO_DO_NOME = 3.25;

const COR_DA_SITUACAO: Record<SituacaoVacinal, { color: 'success' | 'warning' | 'error'; variant: 'filled' | 'outlined' }> = {
  EM_DIA: { color: 'success', variant: 'outlined' },
  A_VENCER: { color: 'warning', variant: 'outlined' },
  PENDENTE: { color: 'warning', variant: 'filled' },
  VENCIDA: { color: 'error', variant: 'filled' },
};

export const SituacaoVacinalChip: React.FC<{ situacao: SituacaoVacinal }> = ({ situacao }) => (
  <Chip
    label={SITUACAO_VACINAL_LABELS[situacao]}
    size="small"
    color={COR_DA_SITUACAO[situacao].color}
    variant={COR_DA_SITUACAO[situacao].variant}
    sx={{ height: 20, fontSize: '0.7rem' }}
  />
);

function ultimaDose(linha: LinhaVacina): string {
  const dose = linha.resultado.ultimaDose;
  if (!dose) return '—';
  const qual = dose.doseKind === 'BOOSTER' ? 'reforço' : `${dose.doseNumber}ª`;
  return `${qual} · ${dataBrasileira(dose.appliedAt)}`;
}

/** O motivo gravado na dose que antecipou o próximo reforço mostrado, quando foi antecipado. */
function motivoDaAntecipacao(linha: LinhaVacina): string | null {
  const proxima = linha.resultado.proximaData;
  if (!proxima) return null;
  const antecipou = linha.doses.find((dose) => dose.nextDueAt === proxima && dose.nextDueReason);
  return antecipou?.nextDueReason ?? null;
}

const Cabecalho: React.FC = () => (
  <Box sx={{ display: 'grid', gridTemplateColumns: COLUNAS, gap: 1, px: 1, pb: 0.5 }}>
    {['Vacina', 'Última dose', 'Próximo', 'Situação'].map((titulo) => (
      <Typography
        key={titulo}
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={titulo === 'Vacina' ? { pl: RECUO_DO_NOME } : undefined}
      >
        {titulo}
      </Typography>
    ))}
  </Box>
);

/** "1ª dose" ou "reforço" — o nome da dose na lista aberta e no rótulo do comprovante. */
function nomeDaDose(dose: Vaccination): string {
  return dose.doseKind === 'BOOSTER' ? 'reforço' : `${dose.doseNumber}ª dose`;
}

/** O servidor guarda o caminho; o arquivo baixado leva só o nome. */
function nomeDoArquivo(proofPath: string): string {
  return proofPath.split(/[\\/]/).pop() ?? proofPath;
}

const DosesDaVacina: React.FC<{ doses: Vaccination[] }> = ({ doses }) => {
  const [erro, setErro] = useState<string | null>(null);
  const emOrdem = [...doses].sort((a, b) => a.appliedAt.localeCompare(b.appliedAt));

  const abrirComprovante = (dose: Vaccination, proofPath: string) => {
    setErro(null);
    vaccinationService
      .downloadProof(String(dose.id), nomeDoArquivo(proofPath))
      .catch(() => setErro('Não foi possível abrir o comprovante.'));
  };

  return (
    <Box sx={{ pl: 4.5, pr: 1, pt: 0.5, pb: 0.75 }}>
      <Stack spacing={0.25} component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {emOrdem.map((dose) => {
          const nome = nomeDaDose(dose);
          const proofPath = dose.proofPath;
          return (
            <Box component="li" key={dose.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 28 }}>
              <Typography variant="caption">
                {`${dose.doseKind === 'BOOSTER' ? 'Reforço' : nome} · ${dataBrasileira(dose.appliedAt)}`}
              </Typography>
              {dose.lot && (
                <Typography variant="caption" color="text.secondary">{`Lote ${dose.lot}`}</Typography>
              )}
              {proofPath && (
                <Tooltip title="Abrir comprovante">
                  <IconButton
                    size="small"
                    aria-label={`Abrir comprovante ${dose.doseKind === 'BOOSTER' ? 'do' : 'da'} ${nome}`}
                    onClick={() => abrirComprovante(dose, proofPath)}
                    sx={{ p: 0.25 }}
                  >
                    <ComprovanteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        })}
      </Stack>
      {erro && (
        <Alert severity="error" variant="outlined" sx={{ mt: 0.5, py: 0 }}>
          {erro}
        </Alert>
      )}
    </Box>
  );
};

const LinhaDaVacina: React.FC<{ linha: LinhaVacina }> = ({ linha }) => {
  const motivo = motivoDaAntecipacao(linha);
  const [aberta, setAberta] = useState(false);
  const temDose = linha.doses.length > 0;
  return (
    <Box sx={{ borderRadius: 1, bgcolor: 'action.hover' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: COLUNAS,
          gap: 1,
          alignItems: 'center',
          py: 0.5,
          px: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          {temDose ? (
            <IconButton
              size="small"
              aria-label={`Ver doses de ${linha.vaccineType.name}`}
              aria-expanded={aberta}
              onClick={() => setAberta((atual) => !atual)}
              sx={{ p: 0.25 }}
            >
              <ExpandMoreIcon
                sx={{ fontSize: 18, transition: 'transform 150ms', transform: aberta ? 'rotate(180deg)' : 'none' }}
              />
            </IconButton>
          ) : (
            <Box sx={{ width: 22, flexShrink: 0 }} />
          )}
          <Typography variant="caption" noWrap title={linha.vaccineType.name}>
            {linha.vaccineType.name}
          </Typography>
        </Box>
        <Typography variant="caption">{ultimaDose(linha)}</Typography>
        <Typography variant="caption">
          {dataBrasileira(linha.resultado.proximaData)}
          {motivo && (
            <Tooltip title={`Antecipado: ${motivo}`}>
              <Box component="span" aria-label={`Reforço antecipado: ${motivo}`} sx={{ ml: 0.5, color: 'warning.main', cursor: 'help' }}>
                *
              </Box>
            </Tooltip>
          )}
        </Typography>
        <SituacaoVacinalChip situacao={linha.resultado.situacao} />
      </Box>
      {temDose && (
        <Collapse in={aberta} unmountOnExit>
          <DosesDaVacina doses={linha.doses} />
        </Collapse>
      )}
    </Box>
  );
};

export const EmployeeVaccinationsCard: React.FC<EmployeeVaccinationsCardProps> = ({ employeeId, companyId, ativo }) => {
  const idDoFuncionario = String(employeeId);
  const carregar = useCallback(() => vaccinationService.getEmployeeCard(idDoFuncionario), [idDoFuncionario]);
  const ficha = useDadoPermitido<FichaDeVacinacao>('vaccination.read', 'Vacinas', carregar);
  const registrarDose = useAcao('vaccination.create', 'Registrar dose');
  const marcarExigidas = useAcao('vaccine-requirement.update', 'Vacinas exigidas');
  const [registrando, setRegistrando] = useState(false);

  const exigidas = ficha.dado?.exigidas ?? [];
  const naoExigidas = ficha.dado?.naoExigidas ?? [];

  const conteudo = (): React.ReactNode => {
    if (!ficha.resolvida) {
      return (
        <Stack spacing={1}>
          {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={32} />)}
        </Stack>
      );
    }
    if (ficha.erro) return <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>{ficha.erro}</Alert>;
    if (ficha.bloqueio) return <Typography variant="body2" color="text.secondary">{ficha.bloqueio}</Typography>;

    return (
      <>
        {exigidas.length > 0 ? (
          <>
            <Cabecalho />
            <Stack spacing={0.75}>
              {exigidas.map((linha) => <LinhaDaVacina key={linha.vaccineType.id} linha={linha} />)}
            </Stack>
          </>
        ) : !ativo ? (
          <Typography variant="body2" color="text.secondary">
            Funcionário fora do efetivo: a situação vacinal é acompanhada só para quem está ativo.
          </Typography>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary">A empresa não exige vacinas</Typography>
            {!marcarExigidas.disabled && (
              <RouteActionButton
                para={`/companies/${companyId}`}
                exige={pode('company.read')}
                size="small"
                sx={{ mt: 0.5, px: 0 }}
              >
                Vacinas exigidas
              </RouteActionButton>
            )}
          </Box>
        )}

        {naoExigidas.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
              Registradas, não exigidas
            </Typography>
            <Stack spacing={0.75}>
              {naoExigidas.map((linha) => <LinhaDaVacina key={linha.vaccineType.id} linha={linha} />)}
            </Stack>
          </Box>
        )}
      </>
    );
  };

  return (
    <Card variant="outlined" sx={{ height: '100%', borderTop: `3px solid ${ACCENT}` }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Box sx={{ color: ACCENT, display: 'flex' }}><VaccinesIcon fontSize="small" /></Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
            Vacinas
          </Typography>
          {ficha.resolvida && !ficha.erro && !ficha.bloqueio && (
            <Chip label={exigidas.length} size="small" sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 700 }} />
          )}
          {!registrarDose.disabled && ficha.dado && (
            <Button size="small" startIcon={<AddIcon />} onClick={() => setRegistrando(true)}>
              Registrar dose
            </Button>
          )}
        </Box>

        {conteudo()}
      </CardContent>

      {registrando && (
        <RegisterDoseDialog
          open
          employeeId={idDoFuncionario}
          linhas={[...exigidas, ...naoExigidas]}
          onClose={() => setRegistrando(false)}
          onSaved={() => {
            setRegistrando(false);
            ficha.recarregar();
          }}
        />
      )}
    </Card>
  );
};

export default EmployeeVaccinationsCard;
